import { RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly } from '../../../../lib/formatDate';
import { BudgetPlanModal } from '../components/BudgetPlanModal';
import { goalsApi } from '../budgetGoalsApi';
import { budgetPlansApi } from '../budgetPlansApi';
import type { BudgetPlanDetail, BudgetPlanListItem } from '../budgetPlanTypes';

/**
 * The budget plans — one per fiscal year, the root of the whole cycle.
 *
 * The **first work surface** in Investment Planning, and the first rail row in the Procurement
 * suite: a reader goes here to do something, where the catalogs behind Settings are what the
 * doing draws on.
 */
export function BudgetPlansIndexPage() {
  const navigate = useNavigate();

  const [editing, setEditing] = useState<BudgetPlanDetail | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    plan: BudgetPlanListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  /*
    A plan cannot be USEFULLY created before a goal exists: its Goal Setting is what a
    department's PPA aligns to, and a plan with none cannot open its submission window. The
    banner says so before the reader opens the form rather than after they fill it.

    Seeded MET, and a failed feed leaves it met — flashing "set up goals first" on every load
    of a working module is worse than a banner arriving a moment late.
  */
  const [hasGoals, setHasGoals] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    goalsApi
      .options(controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setHasGoals(rows.length > 0);
      })
      .catch(() => {
        // Leaves it met.
      });

    return () => controller.abort();
  }, [reloadKey]);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchPlans = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      budgetPlansApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          stage: query.facets.stage?.join(','),
          fiscalYear: query.facets.fiscalYear?.join(','),
        },
        signal,
      ),
    [],
  );

  /** The edit form needs the plan's Goal Setting, which the LIST row does not carry. */
  async function openEditor(plan: BudgetPlanListItem) {
    setError(null);

    try {
      setEditing(await budgetPlansApi.get(plan.id));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Could not open the plan.', 0));
    }
  }

  const rowActions = useCallback(
    (plan: BudgetPlanListItem): MenuItem[] => [
      {
        key: `edit-${plan.id}`,
        label: 'Edit',
        permission: 'budget-plans.update',
        // Disabled for STATE, with the reason — the server refuses it too, and the flag it
        // reads is the same one the endpoint checks.
        disabledReason: plan.isEditable
          ? null
          : `A plan can only be edited while it is a draft or its window is open (this one is ${plan.statusLabel.toLowerCase()}).`,
        onClick: () => void openEditor(plan),
      },
      {
        key: `restore-${plan.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'budget-plans.delete',
        disabledReason: plan.isActive ? 'This plan is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', plan }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') await budgetPlansApi.deactivate(confirming.plan.id);
      else await budgetPlansApi.activate(confirming.plan.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<BudgetPlanListItem>
        module="budget-plans"
        breadcrumb={['Procurement', 'Investment Planning', 'Budget Plans']}
        columns={COLUMNS}
        /*
          TWO state axes, deliberately. `status` is the ROW's own live-or-deleted state — the
          axis `<IndexPage>` reserves, sends at rest and lifts with Show deleted — and `stage`
          is where the plan sits in the budget cycle. They were one key until 22 Sep 2026, and
          the cost was that this list matched nothing on first load while Show deleted did
          nothing at all.
        */
        facets={[
          { key: 'stage', label: 'Stage' },
          { key: 'status', label: 'Status' },
          { key: 'fiscalYear', label: 'Fiscal year' },
        ]}
        // Files by YEAR rather than status: that is the cut a reader takes first here, and
        // they can repoint it from the rail's cog if they disagree.
        folderNavFacet="fiscalYear"
        auditEntityTypes={['BudgetPlan', 'BudgetPlanGoal']}
        rowKey={(plan) => plan.id}
        fetch={fetchPlans}
        reloadKey={reloadKey}
        searchPlaceholder="Search plan numbers, descriptions or notes"
        emptyMessage="No budget plans yet. Create one for the fiscal year being planned."
        prerequisites={[
          {
            label: 'a goal',
            met: hasGoals,
            to: '/procurement/investment-planning/settings/goals',
            permission: 'budget-goals.read',
          },
        ]}
        onCreate={() => setCreating(true)}
        createPermission="budget-plans.create"
        onRowOpen={(plan) => navigate(`/procurement/investment-planning/budget-plans/${plan.id}`)}
        openPermission="budget-plans.read"
        rowActions={rowActions}
        onDelete={(plan) => setConfirming({ kind: 'deactivate', plan })}
        deletePermission="budget-plans.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(plan) => (plan.isActive ? null : 'This plan is already deleted.')}
        onClearData={() => budgetPlansApi.clear()}
        clearDataPermission="budget-plans.clear-data"
        otherInfo={(plan) => plan}
      />

      {creating || editing !== null ? (
        <BudgetPlanModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete budget plan' : 'Restore budget plan'}
          danger={confirming.kind === 'deactivate'}
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`budget-plan-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `${confirming.plan.planNumber} is hidden from this list, and FY ${confirming.plan.fiscalYear} becomes free for another plan.`
                  : `${confirming.plan.planNumber} returns to this list.`}
              </p>

              {confirming.kind === 'deactivate' ? (
                <p>
                  Its Goal Setting is kept exactly as it is, so a restore brings the plan back
                  with the goals it adopted. The plan NUMBER is never reused — a deleted plan
                  keeps it, because every record quoting {confirming.plan.planNumber} must stay
                  unambiguous.
                </p>
              ) : (
                <p>
                  Refused if another active plan has taken FY {confirming.plan.fiscalYear}
                  meanwhile — a deleted plan frees its year.
                </p>
              )}

              {error ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<BudgetPlanListItem>[] = [
  {
    key: 'planNumber',
    header: 'Plan no.',
    width: 150,
    sortable: true,
    // A machine value: mono, never numeric — a code is an identifier, so nothing in it dims.
    render: (plan) => <TextCell value={plan.planNumber} mono />,
  },
  {
    key: 'fiscalYear',
    header: 'FY',
    width: 90,
    align: 'right',
    sortable: true,
    summary: true,
    // A calendar YEAR is an identifier, not a quantity — mono without numeric, the call
    // `PersonnelEducation.YearGraduated` already makes.
    render: (plan) => <TextCell value={String(plan.fiscalYear)} mono />,
  },
  {
    key: 'statusLabel',
    header: 'Stage',
    width: 160,
    sortable: true,
    stage: true,
    /*
      PLAIN TEXT, not a pill, and the column is called Stage rather than Status.

      A plan carries two state concepts — its lifecycle and whether the row is deleted — and
      the disciplinary-record panel already settles how to show both: the lifecycle as plain
      text, and the row's own live/deleted marker as the `status: true` column the shell
      hoists to the end. Two pills would put one word against two meanings in one row.
    */
    render: (plan) => <StatusText tone="inactive">{plan.statusLabel}</StatusText>,
  },
  /*
    THERE IS NO GOALS COLUMN, and its removal is the decision. It counted the goals THIS plan
    had adopted, and a plan adopts nothing now — the Goal Setting tab is the catalog, which is
    the same for every row in this list, so a column of one repeated number says nothing.
  */
  {
    key: 'submissionClosesAt',
    header: 'Submission by',
    width: 150,
    sortable: true,
    /*
      A CALENDAR date, so `formatDateOnly` through a mono TextCell — never `<DateCell>`, which
      renders an instant in the viewer's zone and would tell a reader west of UTC the day
      before the one on the form.
    */
    render: (plan) => <TextCell value={formatDateOnly(plan.submissionClosesAt)} mono />,
  },
  {
    key: 'coverageStartDate',
    header: 'Coverage from',
    width: 150,
    sortable: true,
    render: (plan) => <TextCell value={formatDateOnly(plan.coverageStartDate)} mono />,
  },
  {
    key: 'coverageEndDate',
    header: 'Coverage to',
    width: 150,
    render: (plan) => <TextCell value={formatDateOnly(plan.coverageEndDate)} mono />,
  },
  {
    key: 'description',
    header: 'Description',
    width: 320,
    render: (plan) => <TextCell value={plan.description} />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (plan) =>
      plan.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
